import * as React from "react";
import { Link, useLocation } from "react-router-dom";

import { VideoProjectEditorCard, VideoProjectEditorCardRecord } from "@/components/Cells/VideoProjectEditorCard";

import {
  UsersServices,
  User,
  VideoProjectServices,
  VideoProject,
  VideoProjectQueueSearchParams,
} from "@/services/bedrock";
import { debounce, isEmpty } from "lodash";
import {
  EDITOR_QUEUE_VIEW_STORAGE_KEY,
  EditorQueueView,
  editorQueueListUrl,
  editorQueueViewFromPath,
  extractInitials,
  useSocialCoachSession,
} from "@/utils";
import { Svg } from "@/assets";
import { PageWrapper } from "@/components/PageWrapper";
import { Avatar, Badge, Button, ButtonGroup, Checkbox, Combobox, Input, InputGroup, Pagination } from "@/components/ui";
import { Columns3, LayoutGrid, Search, UserRound } from "lucide-react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { cn } from "@/lib/utils";

import { resolveStaffActorUserId } from "@/components/VideoProjectStaffPicker";
import {
  EditorQueueFilter,
  INCLUDE_DEMO_PROJECTS_STORAGE_KEY,
  parseStoredAssigneeIds,
  parseStoredQueue,
  PRIORITY_ONLY_STORAGE_KEY,
  QUEUE_FILTER_STORAGE_KEY,
  SELECTED_ASSIGNEES_STORAGE_KEY,
  SELECTED_STATUSES_STORAGE_KEY,
  UNASSIGNED_FILTER_VALUE,
} from "./assigneeFilter";
import { EditingQueueKanban, KanbanBoardStats } from "./EditingQueueKanban";
import { QueueFilter } from "./QueueFilter";

interface AssigneeOption {
  key: number;
  text: string;
  value: number;
  avatarUrl?: string;
}

const CARD_WIDTH = 150;
const CARD_GAP = 16;
const ROWS_PER_PAGE = 3;
const STATUS_VALUES = Object.values(VideoProject.Status);

const pageSizeForWidth = (width: number): number => {
  if (width <= 0) return 21;
  const cardsPerRow = Math.max(1, Math.floor((width + CARD_GAP) / (CARD_WIDTH + CARD_GAP)));
  return cardsPerRow * ROWS_PER_PAGE;
};

const estimatePageSize = (): number => {
  if (typeof window === "undefined") return 21;

  const width = window.innerWidth;
  // Sidebar (230px) + page margins (100px) at tablet/desktop; 32px margins on mobile
  const estimatedContentWidth = width >= 768 ? width - 330 : width - 32;
  return pageSizeForWidth(estimatedContentWidth);
};

const videoProjectsWithAssigneeNames = (
  videoProjects: VideoProject[],
  editors: User[]
): VideoProjectEditorCardRecord[] => {
  return videoProjects.map(videoProject => {
    let assigneeName: string | null = null;
    if (videoProject.editorUserId) {
      const editor = editors.find(editor => editor.id === videoProject.editorUserId);
      assigneeName = editor ? `${editor.firstName} ${editor.lastName}` : "...";
    }

    return {
      ...videoProject,
      assigneeName,
    };
  });
};

export function EditorEditingQueueList() {
  const { userId, isEditor } = useSocialCoachSession();
  const currentUserId = resolveStaffActorUserId(userId);
  const location = useLocation();
  const viewMode = editorQueueViewFromPath(location.pathname);
  const isBoardView = viewMode === "board";

  const [searchTerm, setSearchTerm] = React.useState<string | null>(new URLSearchParams(location.search).get("search"));
  const statusOptions = STATUS_VALUES.map(status => ({
    key: status,
    label: VideoProject.StatusMeta[status].label,
    value: status,
  }));
  const [selectedStatuses, setSelectedStatuses] = React.useState<VideoProject.Status[]>(
    localStorage
      .getItem(SELECTED_STATUSES_STORAGE_KEY)
      ?.split("|")
      ?.map(el => el as VideoProject.Status)
      ?.filter(el => STATUS_VALUES.includes(el)) || [VideoProject.Status.SUBMITTED_FOR_EDITING]
  );
  const [selectedAssignees, setSelectedAssignees] = React.useState<number[]>(() =>
    parseStoredAssigneeIds(localStorage.getItem(SELECTED_ASSIGNEES_STORAGE_KEY))
  );
  const [queue, setQueue] = React.useState<EditorQueueFilter>(() =>
    parseStoredQueue(
      localStorage.getItem(QUEUE_FILTER_STORAGE_KEY),
      localStorage.getItem(PRIORITY_ONLY_STORAGE_KEY),
      localStorage.getItem(SELECTED_ASSIGNEES_STORAGE_KEY)
    )
  );
  const [includeDemoProjects, setIncludeDemoProjects] = React.useState<boolean>(
    localStorage.getItem(INCLUDE_DEMO_PROJECTS_STORAGE_KEY) === "true"
  );

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState<number>(estimatePageSize);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    localStorage.setItem(EDITOR_QUEUE_VIEW_STORAGE_KEY, viewMode);
  }, [viewMode]);

  React.useLayoutEffect(() => {
    if (isBoardView) return;

    const el = containerRef.current;
    if (!el) return;

    const updatePageSize = () => {
      const width = el.clientWidth;
      if (width > 0) {
        const targetPageSize = pageSizeForWidth(width);
        setPageSize(prevSize => (prevSize !== targetPageSize ? targetPageSize : prevSize));
      }
    };

    updatePageSize();

    const debouncedUpdate = debounce(updatePageSize, 150);

    const observer = new ResizeObserver(() => {
      debouncedUpdate();
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      debouncedUpdate.cancel();
    };
  }, [isBoardView]);

  const assignees = React.useMemo(() => {
    return selectedAssignees.length > 0
      ? selectedAssignees.map(id => (id === UNASSIGNED_FILTER_VALUE ? null : id))
      : undefined;
  }, [selectedAssignees]);

  const boardFilters = React.useMemo(
    () => ({
      searchTerm,
      includeDemoProjects: !isEditor,
      assignees,
      queue,
    }),
    [searchTerm, isEditor, assignees, queue]
  );

  const gridFilters = React.useMemo<VideoProjectQueueSearchParams>(
    () => ({
      searchTerm,
      includeDemoProjects,
      assignees,
      status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
    }),
    [searchTerm, includeDemoProjects, assignees, selectedStatuses]
  );

  const {
    data: videoProjectsData,
    isLoading: isLoadingVideoProjects,
    isFetching: isFetchingVideoProjects,
  } = useQuery({
    queryKey: ["videoProjects", "grid", gridFilters, page, pageSize],
    queryFn: () =>
      VideoProjectServices.index({
        ...gridFilters,
        page: page + 1,
        perPage: pageSize,
        sort: "id",
        order: "desc",
        extraFields: ["needsAck", "isPriority"],
      }),
    placeholderData: keepPreviousData,
    staleTime: 0,
    enabled: !isBoardView,
  });

  const videoProjects = videoProjectsData?.content || [];
  const totalPages = videoProjectsData?.pagination?.totalPages || 0;
  const totalElements = videoProjectsData?.pagination?.totalCount || 0;

  const [boardStats, setBoardStats] = React.useState<KanbanBoardStats>({
    totalCount: 0,
    totalPriorityCount: 0,
    isInitialLoading: true,
    isSyncing: false,
  });

  const { data: editorsData, isLoading: loadingEditors } = useQuery({
    queryKey: ["editors"],
    queryFn: () => UsersServices.getUsers({ userType: User.Type.EDITOR }),
    staleTime: 1000 * 60 * 30,
  });

  const editors = React.useMemo(() => {
    const rawContent = editorsData?.content || [];
    return [...rawContent].sort((a: User, b: User) => {
      if (a.id === currentUserId) return -1;
      if (b.id === currentUserId) return 1;
      if ((a.firstName || "") < (b.firstName || "")) return -1;
      if ((a.firstName || "") > (b.firstName || "")) return 1;
      return 0;
    });
  }, [editorsData, currentUserId]);

  const assigneeOptions = React.useMemo<AssigneeOption[]>(() => {
    return [
      { key: UNASSIGNED_FILTER_VALUE, text: "Unassigned", value: UNASSIGNED_FILTER_VALUE },
      ...editors.map(editor => ({
        key: editor.id,
        text: `${editor.firstName} ${editor.lastName}`,
        value: editor.id,
        avatarUrl: editor.photoUrl || Svg.noImageuser,
      })),
    ];
  }, [editors]);

  const videoProjectCards = React.useMemo(
    () => videoProjectsWithAssigneeNames(videoProjects, editors),
    [videoProjects, editors]
  );

  const isInitialLoading = isLoadingVideoProjects && !videoProjectsData;
  const loading = isInitialLoading || (loadingEditors && editors.length === 0);

  const delayedSetSearchTerm = React.useRef(
    debounce((text: string) => {
      setPage(0);
      setSearchTerm(isEmpty(text) ? null : text);
    }, 500)
  ).current;

  const onSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    delayedSetSearchTerm(e.target.value);
  };
  const onSelectedStatusesChange = (selectedItems: typeof statusOptions) => {
    const statuses = selectedItems.map(item => item.value);
    localStorage.setItem(SELECTED_STATUSES_STORAGE_KEY, statuses.join("|"));
    setPage(0);
    setSelectedStatuses(statuses);
  };
  const onSelectedEditorsChange = (selectedItems: AssigneeOption[]) => {
    const nextAssignees = selectedItems.map(item => item.value);
    localStorage.setItem(SELECTED_ASSIGNEES_STORAGE_KEY, nextAssignees.join("|"));
    setPage(0);
    setSelectedAssignees(nextAssignees);
  };
  const onQueueChange = (nextQueue: EditorQueueFilter) => {
    localStorage.setItem(QUEUE_FILTER_STORAGE_KEY, nextQueue);
    setPage(0);
    setQueue(nextQueue);
  };
  const onIncludeDemoProjectsChange = (checked: boolean) => {
    localStorage.setItem(INCLUDE_DEMO_PROJECTS_STORAGE_KEY, `${checked}`);
    setPage(0);
    setIncludeDemoProjects(checked);
  };
  const viewHref = (mode: EditorQueueView) => ({
    pathname: editorQueueListUrl(mode),
    search: location.search,
  });

  const assigneeCombobox = (
    <Combobox
      className={cn("shrink-0 bg-white", isBoardView ? "w-[260px]" : "w-full max-w-[200px]")}
      placeholder="Select an assignee"
      multiple
      items={assigneeOptions}
      value={assigneeOptions.filter(opt => selectedAssignees.includes(opt.value))}
      onValueChange={onSelectedEditorsChange}
      itemToStringLabel={item => item.text}
      itemToIcon={item => {
        if (item.value === UNASSIGNED_FILTER_VALUE) return null;
        return (
          <Avatar className="size-5 mr-1.5">
            <Avatar.Image src={item.avatarUrl} />
            <Avatar.Fallback className="text-[8px]">{extractInitials(item.text)}</Avatar.Fallback>
          </Avatar>
        );
      }}
      maxChips="auto"
    />
  );

  const viewModeToggle = (
    <ButtonGroup className="bg-white border border-slate-200 rounded-lg p-0.5">
      <Button
        variant={isBoardView ? "ghost" : "secondary"}
        size="sm"
        className={cn("gap-1.5", !isBoardView && "shadow-xs")}
        nativeButton={false}
        render={<Link to={viewHref("grid")} onClick={() => setPage(0)} />}
      >
        <LayoutGrid className="size-3.5" />
        Grid
      </Button>
      <Button
        variant={isBoardView ? "secondary" : "ghost"}
        size="sm"
        className={cn("gap-1.5", isBoardView && "shadow-xs")}
        nativeButton={false}
        render={<Link to={viewHref("board")} onClick={() => setPage(0)} />}
      >
        <Columns3 className="size-3.5" />
        Board
      </Button>
    </ButtonGroup>
  );

  const countBadge = (
    <Badge variant="outline" size="medium" className="bg-white shadow-xs border-slate-200 py-1.5 px-3">
      <span className="text-slate-500 font-medium">Showing:</span>
      {isBoardView && boardStats.totalPriorityCount > 0 ? (
        <span
          className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-sky-500 px-2 py-0.5 text-xs font-bold text-white"
          title={`${boardStats.totalPriorityCount} in Mine`}
        >
          <UserRound className="size-3 fill-current" aria-hidden="true" />
          {boardStats.totalPriorityCount}
        </span>
      ) : null}
      <span className="font-bold text-slate-900 ml-1.5">{isBoardView ? boardStats.totalCount : totalElements}</span>
      {((isBoardView && boardStats.isSyncing) || (!isBoardView && isFetchingVideoProjects && !isInitialLoading)) && (
        <span className="text-slate-400 font-normal ml-1.5">· syncing</span>
      )}
    </Badge>
  );

  return (
    <PageWrapper title="Editing Queue">
      <div className="mb-6 flex w-full flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
          <InputGroup
            className="max-w-xs w-full bg-white"
            prepend={<Search className="size-4 text-muted-foreground" />}
            control={controlProps => (
              <Input
                {...controlProps}
                data-elm-id="admin-dashboard-text-search"
                defaultValue={searchTerm || ""}
                placeholder="Search by player or project ID..."
                onChange={onSearchTermChange}
              />
            )}
          />
          {isBoardView ? (
            <>
              <QueueFilter value={queue} onChange={onQueueChange} />
              {assigneeCombobox}
            </>
          ) : (
            <>
              <Combobox
                className="max-w-[200px] w-full bg-white"
                placeholder="Select a status"
                multiple
                items={statusOptions}
                value={statusOptions.filter(opt => selectedStatuses.includes(opt.value))}
                onValueChange={onSelectedStatusesChange}
                maxChips="auto"
              />
              {assigneeCombobox}
              <Checkbox
                label="Show test/demo projects"
                checked={includeDemoProjects}
                onCheckedChange={onIncludeDemoProjectsChange}
              />
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {viewModeToggle}
          {isBoardView ? countBadge : null}
        </div>
      </div>

      <div>
        {isBoardView ? (
          <EditingQueueKanban
            boardFilters={boardFilters}
            editors={editors}
            currentUserId={currentUserId}
            onBoardStatsChange={setBoardStats}
          />
        ) : (
          <div ref={containerRef} data-elm-id="videoProjectListElement" className="flex flex-wrap gap-4">
            {videoProjects.length > 0 ? (
              <>
                {videoProjectCards.map(videoProject => (
                  <VideoProjectEditorCard
                    key={"video_project_card_" + videoProject.id}
                    videoProject={videoProject}
                    disabled={isInitialLoading}
                  />
                ))}
                <div className="mt-6 flex justify-center w-full">
                  <Pagination
                    page={page + 1}
                    boundaryRange={3}
                    totalPages={totalPages}
                    disabled={isInitialLoading}
                    onPageChange={p => {
                      setPage(p - 1);
                    }}
                  />
                </div>
              </>
            ) : loading ? (
              "Loading..."
            ) : (
              <div className="resultsEmptyState flex flex-col items-center justify-center text-center py-12 text-slate-500">
                <Search className="size-12 text-slate-400 mb-3" />
                <h2 className="text-xl font-semibold text-slate-900 mb-1">No results found</h2>
                <div className="text-sm">Adjust your filters and try again</div>
              </div>
            )}
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
